import React from "react";
import { CreateGenericPayerDto } from "@inosio/payments-sdk";
import { Box, Button, CircularProgress, Dialog, DialogContent, Grid, Typography, useMediaQuery } from "@mui/material";
import WhiteInosio from "../../../icons/whiteinosio.svg";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { InputField } from "../../../components/InputController";
import CheckBox from "../../../icons/CheckBox.svg";
import UnCheckedBox from "../../../icons/UnCheckedBox.svg";
import TermsDialog from '../../../components/TermsDialog';
import BackIcon from '../../../icons/back1.svg';
import { getInosioTermsAndConditions, selectOnboardingApplicationState } from "../../../pmui/onboarding/sagas/pmonboardingSlice";
import { createGenericPayer, updateGenericPayer, selectQuickPaymentsState, storePropertyAddress } from "../sagas/quickpaymentsSlice";
import { selectPropertyGroupDetailsState } from "../../../shared/sagas/propertyGroups/propertyGroupsSlice";

const useStyles = makeStyles((theme) => ({
  checkboxError: {
    display: "flex",
    flexDirection: "row",
    borderRadius: "24px",
    width: "340px",
    justifyContent:'center',
    padding:'3px'
  },
  termsAndConditionLink: {
    fontFamily: "Work Sans",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    color: "#39A2DB",
    marginTop: "18px",
    cursor: "pointer",
  },
  buttonContainer: {
    width: "340px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  wrapper: {
    margin: "1px",
    position: "relative",
  },
  buttonProgress: {
    color: "#053742",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  globalError: {
    display: "flex",
    flexDirection: "row",
    borderTop: "2px  solid #DB3939",
    borderRadius: "24px",
    padding: "12px 20px",
    width: "100%",
    height: "49px",
    marginTop: "24px",
  },
}))

interface ModalProps {
  open: boolean;
  onContinue: () => void;
  onBackClick: () => void;
}
const AddGenericPayerModal = (props: ModalProps) => {
  const classes = useStyles();
  const { open, onContinue, onBackClick } = props
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const style = {
    height: isOnMobileScreen ? "100%" : "483px",
  };
  const dispatch = useDispatch();
  // const { companyResponse } = useSelector(selectApplicationsState);
  const { propertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const { contract } = useSelector(selectOnboardingApplicationState);
  const { isHandlingGenericPayer, isHandlingGenericPayerSuccess, isHandlingGenericPayerFailed, genericPayerResponse, handlingGenericPayerError, propertyAddress: savedPropertyAddress } = useSelector(selectQuickPaymentsState);

  React.useEffect(() => {
    if (!contract.InosioTsCs.Text) {
      dispatch({
        type: getInosioTermsAndConditions.type
      });
    }
  }, [dispatch]);

  const [firstName, setFirstName] = React.useState("");
  const [firstNameError, setFirstNameError] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [lastNameError, setLastNameError] = React.useState("");
  const [propertyAddress, setPropertyAddress] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [globalError, setGlobalError] = React.useState("");
  const [checkBoxErrorText, setCheckBoxErrorText] = React.useState("");
  const [openInosio, setOpenInosio] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);

  React.useEffect(() => {
    if (!!genericPayerResponse?.FirstName) {
      setFirstName(genericPayerResponse.FirstName);
      setLastName(genericPayerResponse.LastName);
      setEmail(genericPayerResponse.Email);
      setAccepted(true);
    }
    if (!!savedPropertyAddress) {
      setPropertyAddress(savedPropertyAddress);
    }
  }, [genericPayerResponse, savedPropertyAddress]);

  const handleSubmitPayer = () => {
    setGlobalError("");

    if (!firstName) {
      setFirstNameError("FirstName is required.");
    } else {
      setFirstNameError("");
    }
    if (!lastName) {
      setLastNameError("LastName is required.");
    } else {
      setLastNameError("");
    }
    if (!email) {
      setEmailError("Email is required.");
    } else {
      setEmailError("");
    }

    if (!accepted) {
      setCheckBoxErrorText("Please agree to the terms and conditions before proceeding");
    } else {
      setCheckBoxErrorText('');
    }

    if (propertyGroupDetails?.Uuid && firstName && lastName && email && accepted) {
      dispatch({
        type: genericPayerResponse?.Uuid ? updateGenericPayer.type: createGenericPayer.type,
        payload: { 
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          PropertyGroupUuid: propertyGroupDetails?.Uuid || '',
          Uuid: genericPayerResponse?.Uuid,
        } as CreateGenericPayerDto,
      });

      dispatch({
        type: storePropertyAddress.type,
        payload: propertyAddress,
      });
    }
  }

  const handleCloseInosio = () => {
    setOpenInosio(false);
    setAccepted(true)
  };

  React.useEffect(() => {
    if (isHandlingGenericPayerSuccess) {
      onContinue();
    }
    if (isHandlingGenericPayerFailed) {
      setGlobalError(handlingGenericPayerError.message);
    }
  }, [isHandlingGenericPayerSuccess, isHandlingGenericPayerFailed, genericPayerResponse, handlingGenericPayerError])

  return (
    <Dialog
      open={open}
      fullScreen={isOnMobileScreen}
      BackdropProps={{
        invisible: true,
      }}
      fullWidth={true}
      disableEnforceFocus
      style={{
        background: "none",
      }}
      PaperProps={{
        style: {
          pointerEvents: "auto",
          borderRadius: isOnMobileScreen ? "0px" : "24px",
          width: isOnMobileScreen ? "100%" : "472px",
          boxShadow: "none",
          overflow: "hidden"
        },
      }}
      disableEscapeKeyDown={true}
    >
      <DialogContent>
        <Box sx={style}>
          <Grid item display='flex' justifyContent='space-between' flexDirection='column' sx={{ height: '100%' }} >
            <Grid item display="flex" flexDirection="column" alignItems="center">
              {isOnMobileScreen ?
                <Grid item display='flex' justifyContent='center' pl={4} sx={{ width: '340px' }}>
                  <img src={WhiteInosio} style={{ width: "75px", height: "75px" }} alt="logo" />
                  <Typography
                    sx={{
                      fontFamily: "Work Sans",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '18px',
                      lineHeight: '23px',
                      color: '#14142B',
                      textAlign: 'left',
                      padding: '15px'
                    }}>
                    {propertyGroupDetails.CompanyName}
                  </Typography>
                </Grid>
              : <></>}

              {globalError?.length ? (
                <div className={classes.globalError}>
                  <Typography
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#DB3939",
                    }}
                  >
                    {globalError}
                  </Typography>
                </div>
              ) : (
                <></>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 340 }}>
                <Typography id='paymentReq' sx={{
                  fontFamily: "Open Sans",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: '22px',
                  color: '#14142B',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '30px'
                }}>
                  Tell us about yourself.
                </Typography>

                <Grid container direction="row" spacing={1} sx={{ mt: 3 }}>
                  <Grid item xs={12} sm={6} sx={{ mb: -1 }}>
                    <InputField
                      name={"first_name"}
                      placeholder={"First Name"}
                      label={"First Name"}
                      isError={false}
                      errors={firstNameError}
                      touched={true}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ mb: -1 }}>
                    <InputField
                      name={"last_name"}
                      placeholder={"Last Name"}
                      label={"Last Name"}
                      isError={false}
                      errors={lastNameError}
                      touched={true}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ mb: -1 }}>
                    <InputField
                      name={"propertyAddress"}
                      placeholder={"Property Address"}
                      label={"Property Address"}
                      isError={false}
                      errors={''}
                      touched={true}
                      value={propertyAddress}
                      onChange={(e) => setPropertyAddress(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputField
                      name={"email"}
                      placeholder={"Email"}
                      label={"Email"}
                      isError={false}
                      errors={emailError}
                      touched={true}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6 }}>
                    <Typography style={{
                      fontFamily: "Work Sans",
                      paddingLeft: "18px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "22px",
                      marginTop: '10px'
                    }} >
                      Accept <span style={{
                        cursor: 'pointer'
                      }} className={classes.termsAndConditionLink} onClick={() => { setOpenInosio(true) }}>T&Cs</span>
                    </Typography>
                    <Grid ml={1} mt={1.3} item sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <img id="accept-terms" src={accepted ? CheckBox : UnCheckedBox} style={{ width: "13px", height: "13px", cursor: 'pointer' }} alt='Checkmark' onClick={() => setAccepted(!accepted)} />
                    </Grid>
                  </Grid>
                  {checkBoxErrorText.length ? (
                    <div className={classes.checkboxError}>
                      <Typography
                        style={{
                          fontFamily: "BMWHelvetica",
                          fontStyle: "normal",
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "18px",
                          color: "#DB3939",
                        }}
                      >
                        {" "}
                        {checkBoxErrorText}
                      </Typography>
                    </div>
                  ) : (
                    <></>
                  )}
                </Grid>

                <Box className={classes.buttonContainer} sx={{ justifyContent: "flex-end" }}>
                  <Box className={classes.wrapper}>
                    <Button
                      type="submit"
                      variant="contained"
                      style={{
                        width: "113px",
                        height: "49px",
                        borderRadius: "24px",
                        textTransform: "capitalize",
                        color: 'white',
                        backgroundColor: "#39A2DB",
                        borderColor: "#39A2DB",
                        fontFamily: 'Open Sans',
                        fontWeight: '700',
                        fontSize: '18px',
                        lineHeight: '22px',
                      }}
                      onClick={handleSubmitPayer}
                      id="Continue"
                    >
                      Continue
                    </Button>
                    {isHandlingGenericPayer ? <CircularProgress size={26} className={classes.buttonProgress} /> : <></>}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Grid item sx={{ position: 'absolute', top: isOnMobileScreen ? 115 : 40, left: 15 }}>
          <img src={BackIcon} alt="back" style={{ cursor: 'pointer' }} onClick={onBackClick} />
        </Grid>
      </DialogContent>

      <TermsDialog open={openInosio} scroll={true} text={contract.InosioTsCs.Text} title="Inosio Agreement" handleConfirm={handleCloseInosio} buttonText="Agree" />
    </Dialog>
  );
}

export default AddGenericPayerModal;
